import { type Tel, type StartSpanOpts, nullTel, prepareTelError } from '@w/tel'
import { z, type ZodType } from './schema'
import { _dedent, safe } from '@w/utils'
import { Column, type ColumnName } from './column'
import { Condition } from './condition'
import { DbError } from './errors'
import { maskColumnValue } from './redaction'
import type {
  DatabaseDriver,
  DriverCallContext,
  Dialect,
  ParamWithMeta,
  StreamOptions,
  SqlFragment,
} from './types'

const immediateTransactionSql = Symbol.for('@w/db.immediateTransactionSql')

type InternalImmediateDriver = DatabaseDriver & {
  query<T>(
    sql: string,
    params?: unknown[],
    opts?: DriverCallContext & { immediateTransaction?: boolean },
  ): Promise<T[]>
}

/**
 * A map of output key → Column, used as a shorthand schema for execute().
 *
 * @example
 * ```ts
 * // Instead of:
 * execute(z.object({ role: db.users.role.zodType }))
 *
 * // Write:
 * execute({ role: db.users.role })
 * ```
 */
// oxlint-disable-next-line typescript/no-explicit-any
export type ColumnMap = Record<string, Column<any, any, any>>

type ColumnMapColumnType<C> =
  C extends Column<infer T, infer I, infer _G>
    ? I extends 'optional'
      ? T | null
      : T
    : never

/**
 * Infers the TypeScript output type from a ColumnMap.
 * Each key maps to the column's select type (includes nullable columns).
 */
export type ColumnMapType<M extends ColumnMap> = {
  // oxlint-disable-next-line typescript/no-explicit-any
  [K in keyof M]: ColumnMapColumnType<M[K]>
}

/**
 * Converts a ColumnMap to a z.ZodObject schema.
 * @internal
 */
function columnMapToSchema<M extends ColumnMap>(
  map: M,
): ZodType<ColumnMapType<M>> {
  const shape: Record<string, ZodType> = {}
  for (const [key, col] of Object.entries(map)) {
    shape[key] = col.zodType
  }
  return z.object(shape) as ZodType<ColumnMapType<M>>
}

/**
 * Type guard — is the argument a ColumnMap (plain object of Column instances)?
 */
function isColumnMap(value: unknown): value is ColumnMap {
  if (value === null || typeof value !== 'object') return false
  if ('schema' in value) return false
  const entries = Object.values(value)
  return entries.length > 0 && entries.every((v) => v instanceof Column)
}

/**
 * Optional overrides for spans created automatically by query execution methods.
 */
export type SpanOverrides = {
  name?: string
  input?: unknown
}

/**
 * Options for execute/executeOne methods
 */
export type ExecuteOpts<T> = {
  schema: ZodType<T>
  tel?: Tel
  spanOverrides?: SpanOverrides
}

/**
 * Options for run method
 */
export type RunOpts = {
  tel?: Tel
  spanOverrides?: SpanOverrides
}

// Forward declarations for circular dependency resolution
interface TableLike extends SqlFragment {
  __name: string
}

export function isTransactionConflict(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'code' in error &&
    (error.code === 'SQLITE_BUSY_SNAPSHOT' || error.code === 'SQLITE_BUSY')
}

/** The bounded transaction owner reports failure only after its attempts exhaust. */
export function transactionSpan<R>(tel: Tel, managed: boolean, options: StartSpanOpts, run: (tel: Tel) => Promise<R>): Promise<R> {
  return tel.span(options, async spanTel => {
    try {
      const result = await run(spanTel)
      return result
    } catch (error) {
      if (managed && isTransactionConflict(error)) {
        if (options.name === 'db.transaction.attempt') spanTel.warn(prepareTelError({ error, span: spanTel.currentSpan.data, levelOverride: 'warning' }))
        spanTel.currentSpan.end({ error }, { errorLevel: 'warning' })
      }
      throw error
    }
  })
}

/**
 * Represents a constructed SQL query or fragment.
 *
 * Queries can be:
 * - **Executable**: Created via `db.query`, has dialect and driver bound
 * - **Fragment**: Created via `sql`, for composition only, cannot execute
 */
export class Query implements SqlFragment {
  private readonly _dialect: Dialect | undefined
  private readonly _boundDriver: DatabaseDriver | undefined
  private readonly _tel: Tel
  private readonly _dependencies: readonly string[]

  constructor(
    private readonly sqlText: string,
    private readonly params: ParamWithMeta[],
    dialect?: Dialect,
    boundDriver?: DatabaseDriver,
    tel: Tel = nullTel,
    dependencies: readonly string[] = [],
    private readonly immediateTransaction = false,
    private readonly transactionRetryManaged = false,
  ) {
    this._dialect = dialect
    this._boundDriver = boundDriver
    this._tel = tel
    this._dependencies = dependencies
  }

  /**
   * Gets the dialect for this query (undefined for fragments)
   */
  get dialect(): Dialect | undefined {
    return this._dialect
  }

  /**
   * Bound driver, undefined for SQL fragments.
   */
  get driver(): DatabaseDriver | undefined {
    return this._boundDriver
  }

  /** Final SQL and bound values sent to the driver. */
  toSql(): { sql: string; params: unknown[] } {
    return { sql: this._getFinalSql(), params: this.paramValues }
  }

  /** Whether this query has a driver bound. */
  get isExecutable(): boolean {
    return this._boundDriver !== undefined
  }

  /**
   * Gets the SQL text and parameters for this query
   */
  toSQL(_paramOffset: number = 1): { sql: string; params: ParamWithMeta[] } {
    return { sql: this.sqlText, params: this.params }
  }

  /**
   * Converts this query to SQL with a parameter offset for nesting.
   * The parent's dialect is passed in to determine placeholder format.
   */
  toSQLWithOffset(
    paramOffset: number,
    parentDialect: Dialect,
  ): { sql: string; params: ParamWithMeta[] } {
    let sql = this.sqlText
    const currentParams = [...this.params]

    if (
      (parentDialect === 'sqlite' || parentDialect === 'snowflake') &&
      currentParams.length > 0
    ) {
      // For SQLite/Snowflake: replace $N placeholders with ?
      sql = sql.replaceAll(/\$\d+/g, '?')
    } else {
      // For Postgres: renumber $1, $2, etc. with offset
      let paramNum = 1
      sql = sql.replaceAll(/\$(\d+)/g, () => `$${paramOffset + paramNum++ - 1}`)
    }

    return { sql, params: currentParams }
  }

  /**
   * Gets the final SQL with dialect-appropriate placeholders
   */
  private _getFinalSql(): string {
    if (
      (this._dialect === 'sqlite' || this._dialect === 'snowflake') &&
      this.params.length > 0
    ) {
      return this.sqlText.replaceAll(/\$\d+/g, '?')
    }
    return this.sqlText
  }

  /**
   * Gets the raw SQL text (for debugging)
   */
  get text(): string {
    return this.sqlText
  }

  /**
   * Gets the parameter values for driver execution
   */
  get paramValues(): unknown[] {
    return this.params.map((p) => p.value)
  }

  /**
   * Gets the parameter values with metadata (for debugging)
   */
  get values(): ParamWithMeta[] {
    return this.params
  }

  get dependencies(): readonly string[] {
    return this._dependencies
  }

  /**
   * Gets masked parameter values for logging.
   * Values for columns with "id" in their name are preserved;
   * falsy values are preserved; all other values are replaced with "***".
   */
  getMaskedParams(): unknown[] {
    return this.params.map((p) => maskColumnValue(p.value, p.columnName))
  }

  /**
   * Returns a span target label based on the dialect.
   * Snowflake → 'sf', others → 'db'.
   */
  private _getSpanTarget(): 'db' | 'sf' {
    return this._dialect === 'snowflake' ? 'sf' : 'db'
  }

  /**
   * Determines if this query is a read-only query or a mutation.
   * Analyzes the SQL text to classify the operation type.
   */
  private _getQueryType(): 'query' | 'mutation' {
    const firstKeyword =
      /^\s*(SELECT|INSERT|UPDATE|DELETE|WITH|TRUNCATE|CREATE|ALTER|DROP)/i
        .exec(this.sqlText)?.[1]
        ?.toUpperCase()

    // oxlint-disable-next-line typescript-eslint/switch-exhaustiveness-check
    switch (firstKeyword) {
      case 'SELECT':
      case 'WITH':
        return 'query'
      case 'INSERT':
      case 'UPDATE':
      case 'DELETE':
      case 'TRUNCATE':
      case 'CREATE':
      case 'ALTER':
      case 'DROP':
        return 'mutation'
      default:
        // Default to mutation for safety (e.g., unknown DDL commands)
        return 'mutation'
    }
  }

  private _getSpanInput(spanOverrides?: SpanOverrides): unknown {
    if (spanOverrides && 'input' in spanOverrides) {
      return spanOverrides.input
    }
    return { sql: this.sqlText, params: this.getMaskedParams() }
  }

  private _isDefaultSpanInput(spanOverrides?: SpanOverrides): boolean {
    return !(spanOverrides && 'input' in spanOverrides)
  }

  /**
   * Executes the query and returns validated results.
   * Only works for executable queries (created via db.query).
   *
   * @overload execute(schema) - Execute with just a schema (backwards compatible)
   * @overload execute(opts) - Execute with options object containing schema and optional tel
   * @overload execute(columns) - Execute with a column map shorthand (e.g. { role: db.users.role })
   */
  // oxlint-disable-next-line typescript/no-explicit-any
  execute<M extends { [K in keyof M]: Column<any, any, any> }>(
    columns: M,
  ): Promise<ColumnMapType<M>[]>
  execute<T>(schema: ZodType<T>): Promise<T[]>
  execute<T>(opts: ExecuteOpts<T>): Promise<T[]>
  // oxlint-disable-next-line typescript/no-explicit-any
  execute<T>(
    schemaOrOpts: ZodType<T> | ExecuteOpts<T> | ColumnMap,
  ): Promise<T[]> {
    if (!this._boundDriver) {
      throw new Error(
        'Cannot execute SQL fragment. Use db.query`...` to create an executable query.',
      )
    }

    // oxlint-disable-next-line typescript/no-explicit-any
    let schema: ZodType<any>
    let tel: Tel
    let spanOverrides: SpanOverrides | undefined
    if (isColumnMap(schemaOrOpts)) {
      schema = columnMapToSchema(schemaOrOpts)
      tel = this._tel
    } else if (isExecuteOpts(schemaOrOpts)) {
      schema = schemaOrOpts.schema
      tel = schemaOrOpts.tel ?? this._tel
      spanOverrides = schemaOrOpts.spanOverrides
    } else {
      schema = schemaOrOpts
      tel = this._tel
    }

    // oxlint-disable-next-line typescript/no-unsafe-return
    return transactionSpan(tel, this.transactionRetryManaged,
      {
        name: spanOverrides?.name ?? 'db.execute',
        type: this._getQueryType(),
        target: this._getSpanTarget(),
        input: this._getSpanInput(spanOverrides),
        alreadyMasked: this._isDefaultSpanInput(spanOverrides),
      },
      async (spanTel) => {
        const finalSql = this._getFinalSql()
        const results = await safe(this._boundDriver, 'Driver not bound').query<
          Record<string, unknown>
        >(finalSql, this.paramValues, { tel: spanTel })

        // oxlint-disable-next-line typescript/no-unsafe-return typescript/no-unsafe-call typescript/no-unsafe-member-access
        return results.map((row) => schema.parse(row))
      },
    )
  }

  /**
   * Executes the query and returns the first result, or undefined if no rows.
   * Throws DbError({ code: 'data_integrity' }) if more than one row is returned.
   * Only works for executable queries (created via db.query).
   */
  // oxlint-disable-next-line typescript/no-explicit-any
  executeOne<M extends { [K in keyof M]: Column<any, any, any> }>(
    columns: M,
  ): Promise<ColumnMapType<M> | undefined>
  executeOne<T>(schema: ZodType<T>): Promise<T | undefined>
  executeOne<T>(opts: ExecuteOpts<T>): Promise<T | undefined>
  // oxlint-disable-next-line typescript/no-explicit-any
  async executeOne<T>(
    schemaOrOpts: ZodType<T> | ExecuteOpts<T> | ColumnMap,
  ): Promise<T | undefined> {
    // oxlint-disable-next-line typescript/no-explicit-any
    let schema: ZodType<any>
    let tel: Tel
    let spanOverrides: SpanOverrides | undefined
    if (isColumnMap(schemaOrOpts)) {
      schema = columnMapToSchema(schemaOrOpts)
      tel = this._tel
    } else if (isExecuteOpts(schemaOrOpts)) {
      schema = schemaOrOpts.schema
      tel = schemaOrOpts.tel ?? this._tel
      spanOverrides = schemaOrOpts.spanOverrides
    } else {
      schema = schemaOrOpts
      tel = this._tel
    }

    const results = await this.execute({ schema, tel, spanOverrides })
    if (results.length > 1) {
      throw new DbError(
        `executeOne: expected 0 or 1 rows, got ${results.length}`,
        {
          code: 'DATA_INTEGRITY',
        },
      )
    }
    // oxlint-disable-next-line typescript/no-unsafe-return
    return results[0]
  }

  /**
   * Executes the query and returns exactly one result.
   * Throws DbError({ code: 'NOT_FOUND' }) if no rows are returned.
   * Throws DbError({ code: 'DATA_INTEGRITY' }) if more than one row is returned.
   * Only works for executable queries (created via db.query).
   */
  // oxlint-disable-next-line typescript/no-explicit-any
  executeOneOrThrow<M extends { [K in keyof M]: Column<any, any, any> }>(
    columns: M,
  ): Promise<ColumnMapType<M>>
  executeOneOrThrow<T>(schema: ZodType<T>): Promise<T>
  executeOneOrThrow<T>(opts: ExecuteOpts<T>): Promise<T>
  // oxlint-disable-next-line typescript/no-explicit-any
  async executeOneOrThrow<T>(
    schemaOrOpts: ZodType<T> | ExecuteOpts<T> | ColumnMap,
  ): Promise<T> {
    // oxlint-disable-next-line typescript/no-explicit-any
    const result = (await this.executeOne(schemaOrOpts as any)) as T | undefined
    if (result === undefined) {
      throw new DbError('executeOneOrThrow: query returned no rows', {
        code: 'NOT_FOUND',
      })
    }
    return result
  }

  /**
   * Executes the query without returning results.
   * Only works for executable queries (created via db.query).
   *
   * @overload run() - Execute without options (backwards compatible)
   * @overload run(opts) - Execute with options object containing optional tel
   */
  run(): Promise<void>
  run(opts: RunOpts): Promise<void>
  run(opts?: RunOpts): Promise<void> {
    if (!this._boundDriver) {
      throw new Error(
        'Cannot execute SQL fragment. Use db.query`...` to create an executable query.',
      )
    }

    const tel = opts?.tel ?? this._tel
    const boundDriver = safe(
      this._boundDriver,
      'Driver not bound',
    ) as InternalImmediateDriver

    return transactionSpan(tel, this.transactionRetryManaged,
      {
        name: opts?.spanOverrides?.name ?? 'db.run',
        type: this._getQueryType(),
        target: this._getSpanTarget(),
        input: this._getSpanInput(opts?.spanOverrides),
        alreadyMasked: this._isDefaultSpanInput(opts?.spanOverrides),
      },
      async (spanTel) => {
        const finalSql = this._getFinalSql()
        await boundDriver.query(finalSql, this.paramValues, {
          immediateTransaction: this.immediateTransaction,
          tel: spanTel,
        })
      },
    )
  }

  /**
   * Streams query results row by row as an async iterable.
   * Each row is validated against the provided Zod schema.
   * Only works for executable queries with drivers that support streaming.
   */
  stream<T>(schema: ZodType<T>, options?: StreamOptions): AsyncGenerator<T>
  // oxlint-disable-next-line typescript/no-explicit-any
  stream<M extends { [K in keyof M]: Column<any, any, any> }>(
    columns: M,
    options?: StreamOptions,
  ): AsyncGenerator<ColumnMapType<M>>
  // oxlint-disable-next-line typescript/no-explicit-any
  async *stream<T>(
    schemaOrColumns: ZodType<T> | ColumnMap,
    options?: StreamOptions,
  ): AsyncGenerator<T> {
    const schema = (
      isColumnMap(schemaOrColumns)
        ? columnMapToSchema(schemaOrColumns)
        : schemaOrColumns
    ) as ZodType<T>
    if (!this._boundDriver) {
      throw new Error(
        'Cannot stream SQL fragment. Use db.query`...` to create an executable query.',
      )
    }
    if (!this._boundDriver.stream) {
      throw new Error(
        'Driver does not support streaming. Use execute() instead.',
      )
    }
    const finalSql = this._getFinalSql()
    for await (const row of this._boundDriver.stream(
      finalSql,
      this.paramValues,
      options,
    )) {
      yield schema.parse(row)
    }
  }

}

/**
 * Type guard for ExecuteOpts
 */
// oxlint-disable-next-line typescript/no-explicit-any
function isExecuteOpts(value: unknown): value is ExecuteOpts<any> {
  return value !== null && typeof value === 'object' && 'schema' in value
}

/**
 * Creates a tagged template literal function for building SQL queries with a specific dialect.
 *
 * @param dialect - The SQL dialect to use for parameter placeholders
 * @param driver - Driver to bind for query execution
 * @param tel - Telemetry instance for tracing queries
 */
export function createSqlTag(
  dialect: Dialect,
  driver: DatabaseDriver,
  tel: Tel = nullTel,
  transactionRetryManaged = false,
): (strings: TemplateStringsArray, ...values: unknown[]) => Query {
  return function sqlTag(
    strings: TemplateStringsArray,
    ...values: unknown[]
  ): Query {
    return buildQuery(
      strings,
      values,
      dialect,
      driver,
      tel,
      transactionRetryManaged,
    )
  }
}

/**
 * Builds a Query from template literal parts.
 * @internal
 */
function buildQuery(
  strings: TemplateStringsArray,
  values: unknown[],
  dialect?: Dialect,
  boundDriver?: DatabaseDriver,
  tel: Tel = nullTel,
  transactionRetryManaged = false,
): Query {
  const parts: string[] = []
  const params: ParamWithMeta[] = []
  const dependencies = new Set<string>()
  let immediateTransaction = false
  let paramIndex = 1

  // Bound queries render with their driver dialect. Unbound `sql` fragments
  // pass `undefined` so each table/column falls back to its own dialect;
  // forcing postgres here would render schema-qualified identifiers in the
  // wrong shape when the fragment is spliced into a sqlite/snowflake query.
  // Placeholders are renumbered per dialect by the outer query anyway.
  const effectiveDialect = dialect

  strings.forEach((str, i) => {
    parts.push(str)

    if (i < values.length) {
      const value = values[i]

      if (value === undefined || value === null || value === false) {
        // Falsy values output nothing - enables && short-circuit pattern
        return
      }

      if (isTable(value)) {
        const { sql: tableSql } = value.toSQL(paramIndex, effectiveDialect)
        parts.push(tableSql)
        dependencies.add(value.__name)
      } else if (value instanceof Column) {
        parts.push(value.toSQL(paramIndex, effectiveDialect).sql)
      } else if (value instanceof Condition) {
        const { sql: condSql, params: condParams } = value.toSQL(
          paramIndex,
          effectiveDialect,
        )
        parts.push(condSql)
        params.push(...condParams)
        paramIndex += condParams.length
      } else if (value instanceof Query) {
        // Nested query/fragment - merge in with renumbered parameters
        const { sql: nestedSql, params: nestedParams } = value.toSQLWithOffset(
          paramIndex,
          effectiveDialect ?? 'postgres',
        )
        parts.push(nestedSql)
        params.push(...nestedParams)
        for (const dependency of value.dependencies) {
          dependencies.add(dependency)
        }
        immediateTransaction ||= (
          value as unknown as { readonly immediateTransaction: boolean }
        ).immediateTransaction
        paramIndex += nestedParams.length
      } else if (isSqlFragment(value)) {
        // InsertHelper, UpdateHelper, or other SqlFragment
        const { sql: fragSql, params: fragParams } = value.toSQL(
          paramIndex,
          effectiveDialect,
        )
        parts.push(fragSql)
        params.push(...fragParams)
        immediateTransaction ||= isImmediateTransactionFragment(value)
        paramIndex += fragParams.length
      } else {
        // Raw value → parameter (no column name known)
        parts.push(`$${paramIndex++}`)
        params.push({ value, columnName: undefined })
      }
    }
  })

  const finalSql = _dedent(parts.join(''))
  return new Query(
    finalSql,
    params,
    dialect,
    boundDriver,
    tel,
    Array.from(dependencies),
    immediateTransaction,
    transactionRetryManaged,
  )
}

/**
 * Type guard for Table-like objects
 */
function isTable(value: unknown): value is TableLike {
  return (
    value !== null &&
    typeof value === 'object' &&
    '__name' in value &&
    typeof (value as TableLike).__name === 'string' &&
    '__columns' in value &&
    'toSQL' in value &&
    typeof (value as TableLike).toSQL === 'function'
  )
}

/**
 * Type guard for SqlFragment
 */
function isSqlFragment(value: unknown): value is SqlFragment {
  return (
    value !== null &&
    typeof value === 'object' &&
    'toSQL' in value &&
    typeof (value as SqlFragment).toSQL === 'function'
  )
}

function isImmediateTransactionFragment(value: SqlFragment): boolean {
  return (
    (value as { readonly [immediateTransactionSql]?: unknown })[
      immediateTransactionSql
    ] === true
  )
}

/**
 * Creates a raw SQL fragment that will not be parameterized.
 * Use with caution - only for trusted, non-user input.
 */
function rawImpl(sqlText: string): SqlFragment
function rawImpl(
  strings: TemplateStringsArray,
  ...values: (SqlFragment | ColumnName)[]
): SqlFragment
function rawImpl(
  sqlTextOrStrings: string | TemplateStringsArray,
  ...values: (SqlFragment | ColumnName)[]
): SqlFragment {
  if (typeof sqlTextOrStrings !== 'string') {
    const strings = sqlTextOrStrings
    return {
      toSQL(
        paramOffset: number,
        dialect?: Dialect,
      ): {
        sql: string
        params: ParamWithMeta[]
      } {
        const parts: string[] = []
        const params: ParamWithMeta[] = []
        for (let i = 0; i < strings.length; i++) {
          parts.push(strings[i] ?? '')
          const value = values[i]
          if (!value) continue
          if (typeof value === 'string') {
            parts.push(value)
            continue
          }
          const rendered = value.toSQL(paramOffset + params.length, dialect)
          parts.push(rendered.sql)
          params.push(...rendered.params)
        }
        return { sql: parts.join(''), params }
      },
    }
  }

  const sqlText = sqlTextOrStrings
  return {
    toSQL(_paramOffset: number): { sql: string; params: ParamWithMeta[] } {
      return { sql: sqlText, params: [] }
    },
  }
}

/**
 * Tagged template literal for building SQL fragments for composition.
 *
 * Creates Query fragments that can be nested within `db.query` calls.
 * Fragments **cannot be executed directly** - they are for composition only.
 *
 * @example
 * ```ts
 * // Build conditional query parts
 * async function searchUsers(db: Database, filters: { role?: string }) {
 *   return db.query`
 *     SELECT ${db.users.all} FROM ${db.users}
 *     WHERE TRUE
 *       ${filters.role && sql`AND ${db.users.role.eq(filters.role)}`}
 *   `.execute(db.users.__selectSchema);
 * }
 * ```
 */
interface SqlTag {
  (strings: TemplateStringsArray, ...values: unknown[]): Query
  /**
   * Creates a raw SQL fragment that will not be parameterized.
   * Use with caution - only for trusted, non-user input.
   */
  raw(sqlText: string): SqlFragment
  raw(
    strings: TemplateStringsArray,
    ...values: (SqlFragment | ColumnName)[]
  ): SqlFragment
}

export const sql: SqlTag = Object.assign(
  function sql(strings: TemplateStringsArray, ...values: unknown[]): Query {
    // Create an unbound fragment - no dialect, no driver
    // Dialect will be inherited from parent db.query when nested
    return buildQuery(strings, values)
  },
  { raw: rawImpl },
)
